import { FILE_STATUS } from 'common/constants.files';

export const getFilename = (url: string) => {
  if (typeof url !== 'string') return '';
  try {
    return url.split('/').pop() || '';
  } catch {
    return '';
  }
};

export const getFileExtension = (fileName: string) => {
  const parts = fileName?.split('.');
  const extension = parts?.length > 1 ? parts.pop()?.toLowerCase() : '';
  return extension || '';
};

export const isPDFFile = (fileExtension: string): boolean => {
  const fileExtensions = ['PDF'];
  return fileExtensions.some((ext) => fileExtension?.toUpperCase() == ext);
};

export const isImageFile = (fileExtension: string): boolean => {
  const imageExtensions = ['JPG', 'JPEG', 'PNG', 'GIF', 'BMP', 'WEBP', 'SVG'];
  return imageExtensions.some((ext) => fileExtension?.toUpperCase() == ext);
};

export const convertToBase64 = async (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error('Invalid file type. Expected a File object.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file); // Encodes file as Base64
  });
};

const createMockFile = (name: string, content = '', mock = false, type = 'text/plain'): File => {
  const blob = new Blob([content], { type });
  const file = new File([blob], name, { type }) as File;
  return file;
};

export const hasUploadFileIdentity = (file: any) =>
  Boolean(file) &&
  typeof file === 'object' &&
  Boolean(file.uid || file.id || file.originFileObj || file.name || file.url || file.thumbUrl || file.previewUrl);

export const normalizeUploadFileListEvent = (event: any) => {
  const nextFileList = Array.isArray(event) ? event : event?.fileList;

  if (!Array.isArray(nextFileList)) {
    return nextFileList;
  }

  return nextFileList
    .filter((file: any) => {
      const isRemoved = file?.status === FILE_STATUS.REMOVED;

      if (isRemoved && typeof file?.previewUrl === 'string' && file.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(file.previewUrl);
      }

      return !isRemoved;
    })
    .map((file: any) => {
      if (file?.previewUrl || file?.mock) {
        return file;
      }

      const originFile = file?.originFileObj;
      if (!(originFile instanceof Blob) || typeof URL?.createObjectURL !== 'function') {
        return {
          ...file,
          previewUrl: file?.url ?? file?.thumbUrl ?? '',
        };
      }

      try {
        return {
          ...file,
          previewUrl: URL.createObjectURL(originFile),
        };
      } catch (error) {
        return {
          ...file,
          previewUrl: file?.url ?? file?.thumbUrl ?? '',
        };
      }
    });
};

export const fetchFiles = async (items: any[]) => {
  if (!items) return [];

  const buildUploadFile = (item: any, originFileObj?: File) => {
    const url = item.file_url || item.file_path;
    const name = item.file_name || getFilename(url);

    return {
      id: (item?.id || '').toString(),
      name: name,
      extension: item?.file_extension,
      status: 'done',
      mock: true,
      ...(originFileObj ? { originFileObj } : {}),
      url,
      previewUrl: url,
      file_category_ids: (item?.file_categories ?? []).map((category: any) => category.id),
      suggested_file_category_ids: (item?.analysis?.suggested_file_categories ?? item?.file_categories ?? []).map(
        (category: any) => category.id
      ),
      summary: item?.analysis?.summary ?? '',
    };
  };

  const resolvedFiles = await Promise.all(
    items
      .filter((item: any) => item.file_path || item.file_name || item.file_url)
      .map(async (item) => {
        const url = item.file_url || item.file_path;
        const name = item.file_name || getFilename(url);

        if (!url) {
          return buildUploadFile(item);
        }

        try {
          const response = await fetch(url);
          const blob = await response.blob();

          const file = new File([blob], name, {
            type: blob.type || 'application/octet-stream',
            lastModified: Date.now(),
          });

          return buildUploadFile(item, file);
        } catch (error) {
          console.error(`Failed to load file from ${url}`, error);
          return buildUploadFile(item);
        }
      })
  );

  return resolvedFiles;
};

export const fetchPhotos = async (items: any[]) => {
  if (!items) return;

  const resolvedFiles = items
    .filter((item: any) => !!item.url)
    .map((item) => {
      return {
        id: (item?.id || '').toString(),
        name: item?.image_name ?? '',
        extension: item?.file_extension,
        status: 'done',
        mock: true,
        category: item.image_category,
        originFileObj: createMockFile(item.image_name, 'File Mock', true), // Store Image object
        url: item.url,
      };
    });

  return resolvedFiles;
};
